/**
 * useNotifications.ts
 *
 * Responsabilidade: Gerenciar permissões e envio de notificações nativas do navegador.
 * Princípios:
 * - Single Responsibility: Apenas gerencia Notifications API.
 * - Progressive Enhancement: Verifica suporte e permissão antes de tentar usar.
 */

import { useState, useCallback, useEffect } from 'react';
import { callPushSubscribe } from '../services/modules/push/push.api';
import {
  isWebPushSupported,
  vapidApplicationServerKey,
} from '../services/modules/push/push.helpers';

const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
export const PUSH_ENDPOINT_STORAGE_KEY = 'adlitics_push_endpoint';

async function getExistingSubscription() {
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

function getSubscriptionKeys(sub: PushSubscription) {
  const json = sub.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!p256dh || !auth) {
    throw new Error('Chaves de push incompletas. Tente novamente.');
  }
  return { endpoint: sub.endpoint, p256dh, auth };
}

function isServerRegistered(endpoint: string) {
  return localStorage.getItem(PUSH_ENDPOINT_STORAGE_KEY) === endpoint;
}

function showLocalConfirmation() {
  try {
    const n = new Notification('Notificações ativadas', {
      body: 'Você receberá alertas de mensagens e tarefas com o app fechado.',
      icon: '/icons/icon-192.png',
      badge: '/icons/icon-192.png',
      tag: 'adlitics-push-enabled',
    });
    window.setTimeout(() => n.close(), 5000);
  } catch {
    void navigator.serviceWorker.ready.then((reg) =>
      reg.showNotification('Notificações ativadas', {
        body: 'Você receberá alertas de mensagens e tarefas com o app fechado.',
        icon: '/icons/icon-192.png',
        badge: '/icons/icon-192.png',
        tag: 'adlitics-push-enabled',
      }),
    );
  }
}

export const useNotifications = () => {
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [pushEnabled, setPushEnabled] = useState(false);
  const [isPushLoading, setIsPushLoading] = useState(false);

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  useEffect(() => {
    if (!isWebPushSupported()) return;

    let cancelled = false;

    const syncPushState = async () => {
      try {
        const sub = await getExistingSubscription();
        // Só considera ativo se o endpoint também foi confirmado no servidor
        // (evita "Desativar" fantasma após 401 no upsert).
        const enabled = !!sub && isServerRegistered(sub.endpoint);
        if (!cancelled) setPushEnabled(enabled);
      } catch {
        if (!cancelled) setPushEnabled(false);
      }
    };

    void syncPushState();
    return () => {
      cancelled = true;
    };
  }, []);

  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.warn('This browser does not support desktop notification');
      return;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }, []);

  const sendNotification = useCallback((title: string, options?: NotificationOptions) => {
    if (permission === 'granted' && document.hidden) {
      try {
        new Notification(title, {
          icon: '/icons/icon-192.png',
          badge: '/icons/icon-192.png',
          ...options,
        });
      } catch (error) {
        console.error('Error sending notification:', error);
      }
    }
  }, [permission]);

  const upsertSubscription = useCallback(async (organizationId: string, sub: PushSubscription) => {
    const { endpoint, p256dh, auth } = getSubscriptionKeys(sub);
    await callPushSubscribe({
      action: 'upsert',
      endpoint,
      p256dh,
      auth,
      organizationId,
      userAgent: navigator.userAgent,
    });
    localStorage.setItem(PUSH_ENDPOINT_STORAGE_KEY, endpoint);
    setPushEnabled(true);
    return endpoint;
  }, []);

  /**
   * Reenvia inscrição local órfã ao servidor (ex.: tentativas anteriores com 401).
   * Retorna 'healed' se registrou agora, 'ok' se já estava ok, 'none' se não há sub local.
   */
  const reconcilePushRegistration = useCallback(async (
    organizationId: string,
  ): Promise<'healed' | 'ok' | 'none' | 'error'> => {
    if (!isWebPushSupported() || !vapidPublicKey) return 'none';

    try {
      const sub = await getExistingSubscription();
      if (!sub) {
        localStorage.removeItem(PUSH_ENDPOINT_STORAGE_KEY);
        setPushEnabled(false);
        return 'none';
      }

      if (isServerRegistered(sub.endpoint)) {
        setPushEnabled(true);
        return 'ok';
      }

      await upsertSubscription(organizationId, sub);
      return 'healed';
    } catch (error) {
      console.error('Error reconciling push registration:', error);
      setPushEnabled(false);
      return 'error';
    }
  }, [upsertSubscription]);

  const enablePush = useCallback(async (
    organizationId: string,
  ): Promise<'granted' | 'denied' | 'unsupported'> => {
    if (!isWebPushSupported() || !vapidPublicKey) {
      return 'unsupported';
    }

    setIsPushLoading(true);

    try {
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result !== 'granted') {
        return 'denied';
      }

      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();

      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: vapidApplicationServerKey(vapidPublicKey),
        });
      }

      await upsertSubscription(organizationId, sub);
      showLocalConfirmation();
      return 'granted';
    } catch (error) {
      console.error('Error enabling push notifications:', error);
      setPushEnabled(false);
      throw error instanceof Error ? error : new Error('Falha ao ativar notificações');
    } finally {
      setIsPushLoading(false);
    }
  }, [upsertSubscription]);

  const disablePush = useCallback(async () => {
    if (!isWebPushSupported()) return;

    setIsPushLoading(true);

    try {
      const sub = await getExistingSubscription();
      const endpoint = sub?.endpoint ?? localStorage.getItem(PUSH_ENDPOINT_STORAGE_KEY);

      if (sub) await sub.unsubscribe();

      if (endpoint) {
        try {
          await callPushSubscribe({ action: 'delete', endpoint });
        } catch {
          /* best-effort: limpa local mesmo se delete remoto falhar */
        }
        localStorage.removeItem(PUSH_ENDPOINT_STORAGE_KEY);
      }

      setPushEnabled(false);
    } catch (error) {
      console.error('Error disabling push notifications:', error);
      throw error;
    } finally {
      setIsPushLoading(false);
    }
  }, []);

  const updatePushPresence = useCallback(async (activeConversationId: string | null) => {
    const endpoint = localStorage.getItem(PUSH_ENDPOINT_STORAGE_KEY);
    if (!endpoint) return;

    try {
      await callPushSubscribe({
        action: 'presence',
        endpoint,
        activeConversationId,
      });
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      // Presence é best-effort — sessão expirada / offline não deve poluir o console.
      if (/não autenticado|não autorizado|faça login|failed to fetch|network/i.test(msg)) {
        return;
      }
      console.warn('Error updating push presence:', msg);
    }
  }, []);

  return {
    permission,
    requestPermission,
    sendNotification,
    pushEnabled,
    isPushLoading,
    enablePush,
    disablePush,
    updatePushPresence,
    reconcilePushRegistration,
  };
};
